import { http } from '@/api/http';
import type { OperationResult, Schemas } from '@/api/shared';
import type { AdoptedStatus, CarrierStatus, ChildlessStatus } from '@/models/patient';

/**
 * Person record endpoints — fetch, create, demographics, and metadata. The
 * `Live*` types in this file capture drift between the live PhenoTips BFF and
 * the checked-in OpenAPI contract; replace them with `Schemas['...']` once the
 * contract is reconciled.
 */

export type PersonDto = Schemas['Person'];
export type PersonSummaryDto = Schemas['PersonSummary'];

/**
 * Live PhenoTips POST body for `/persons`.
 *
 * Drifts from the OpenAPI contract (which still types this endpoint with a
 * flat snake_case shape — `first_name`, `last_name`, `gender_identity`,
 * etc.). The live BFF accepts a Person-shaped body with camelCase nested
 * `demographics` and `personIdentifiers`, mirroring the GET `/persons`
 * response. The `sex` enum also drifts: live `SexOptions` accepts
 * `[m, f, other, unknown, unassigned, intersex]` (single letters for the
 * common cases) where the OpenAPI snapshot still says
 * `'male' | 'female' | 'other' | 'unknown'`. Tracked as a follow-up against
 * the contract; until reconciled, the shape below is the source of truth.
 */
export type LiveSexCode = 'm' | 'f' | 'other' | 'unknown' | 'unassigned' | 'intersex';
/** @deprecated Alias kept for the create flow; new code should use `LiveSexCode`. */
export type CreatePersonSexCode = LiveSexCode;

/**
 * Tri-state interpreter flag accepted by `DemographicsDTO.interpreterRequired`.
 * Uppercase only (`'Y' | 'N' | 'U'`); the BE rejects lowercase / other values.
 */
export type LiveInterpreterRequired = 'Y' | 'N' | 'U';

/** One term inside `EthnicityDTO.terms`. `termId` is a HANCESTRO id when the
 *  picker matched a vocabulary term; null/absent for custom (free-text) entries. */
export interface EthnicityTermDto {
  termId?: string | null;
  /** Required by the BE (`@NotBlank`, `minLength: 1`). */
  label: string;
}

export interface EthnicityDto {
  id?: string;
  /** Required by the BE (`@NotNull`). */
  type: 'PATIENT' | 'PATERNAL' | 'MATERNAL';
  /** `uniqueItems: true` on the wire; the FE de-dupes by `termId || label`. */
  terms?: EthnicityTermDto[];
  source?: Schemas['Source'];
}

/**
 * Physical chart pointer block on `DemographicsDTO.physical_chart`. Snake-case
 * key per the BE swagger; `staff_member_user_guid` is set only when the chosen
 * location refers to a specific staff member.
 */
export interface PhysicalChartDto {
  id?: string;
  date?: string | null;
  location?: string | null;
  staff_member_user_guid?: string | null;
  notes?: string | null;
}

/**
 * Demographics body shape that goes on the wire for both POST `/persons` and
 * PUT `/persons/{guid}/demographics`. Same field set as the OpenAPI schema's
 * `Demographics` but with the sex enum overridden to `LiveSexCode` and the
 * PTV2-1064 additions (ethnicities, physical_chart, four scalars) bolted on
 * until the checked-in schema picks them up.
 */
export type LiveDemographicsBody = Omit<Schemas['Demographics'], 'sex'> & {
  sex?: LiveSexCode;
  primaryCareProvider?: string | null;
  referringProvider?: string | null;
  interpreterRequired?: LiveInterpreterRequired | null;
  interpreterLanguage?: string | null;
  ethnicities?: EthnicityDto[];
  /** Omit to preserve the stored chart; send `null` (or a fully-blank block) to clear it. */
  physical_chart?: PhysicalChartDto | null;
};

/** Wire body for `PATCH /persons/{guid}/pedigree-attributes` (PTV2-1157). Null clears. */
export interface PedigreeAttributesBody {
  readonly carrierStatus: CarrierStatus | null;
  readonly adoptedStatus: AdoptedStatus | null;
  readonly childlessStatus: ChildlessStatus | null;
  /** Heredity note (PTV2-1584). The BE clears it whenever childlessStatus is cleared. */
  readonly childlessReason: string | null;
  readonly evaluated: boolean | null;
}

export interface CreatePersonInput {
  readonly type: 'INDIVIDUAL' | 'PATIENT' | 'FAMILY_MEMBER';
  readonly demographics: LiveDemographicsBody;
  readonly personIdentifiers?: Schemas['PersonIdentifier'][];
}

/**
 * Care-team member shape and the local extension of the generated
 * `PersonMetadata` schema. The OpenAPI contract doesn't include
 * `care_team` yet (PTV2-975 follow-up); this lets fixtures, mappers and
 * the mutation hook stay typed end-to-end without touching the
 * generated `schema.ts`.
 */
export interface CareTeamMemberDto {
  readonly guid: string;
  readonly name: string;
  readonly role: string | null;
}

export type PersonMetadataWithCareTeamDto = Schemas['PersonMetadata'] & {
  care_team?: CareTeamMemberDto[];
};

/**
 * Wire body for `PUT /persons/{guid}/sensitive` (PTV2-1182). Server-owned
 * `flagged`, `set_by`, `set_at` fields are read-only; only `reason` is sent
 * on write, and the BE requires it non-blank when flipping to sensitive.
 */
export interface SensitivityBody {
  readonly reason: string;
}

export const patientApi = {
  /** GET /persons/{guid} — patient core record. */
  getPerson: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<OperationResult<'getPerson'>> =>
    http.get<OperationResult<'getPerson'>>(`/persons/${encodeURIComponent(guid)}`, options),

  /**
   * PUT /persons/{guid}/sensitive — flag the record sensitive with a
   * required non-blank reason. Returns the full updated PersonDTO so the
   * caller can refresh `sensitivity.flagged`/`setBy`/`setAt` in one round-trip.
   */
  setSensitive: (
    guid: string,
    body: SensitivityBody,
    options?: { signal?: AbortSignal },
  ): Promise<PersonDto> =>
    http.put<PersonDto>(`/persons/${encodeURIComponent(guid)}/sensitive`, body, options),

  /** DELETE /persons/{guid}/sensitive — clear the flag + reason + audit stamps. */
  clearSensitive: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.delete<undefined>(`/persons/${encodeURIComponent(guid)}/sensitive`, options),

  /**
   * POST /persons/{guid}/break-glass/acknowledge — auto-audited (PTV2-1036 +
   * S6 BREAK_GLASS tag). No response body; the BE holds no acknowledgment
   * state — the FE gate is UI-only, so the raw API path stays reachable.
   */
  acknowledgeBreakGlass: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<undefined> =>
    http.post<undefined>(
      `/persons/${encodeURIComponent(guid)}/break-glass/acknowledge`,
      undefined,
      { ...options, responseType: 'none' },
    ),

  /** POST /persons — create a new patient record. Returns 201 with the created Person. */
  createPerson: (
    body: CreatePersonInput,
    options?: { signal?: AbortSignal },
  ): Promise<PersonDto> => http.post<PersonDto>('/persons', body, options),

  /**
   * PATCH /persons/{guid}/pedigree-attributes — replace the pedigree-display glyph attributes
   * (carrier / adopted / childless status + evaluated) that live on the person but are edited only
   * from the pedigree (PTV2-1157). Full-replace: null clears. camelCase wire, matching PersonDTO.
   */
  updatePedigreeAttributes: (
    guid: string,
    body: PedigreeAttributesBody,
    options?: { signal?: AbortSignal },
  ): Promise<PedigreeAttributesBody> =>
    http.patch<PedigreeAttributesBody>(
      `/persons/${encodeURIComponent(guid)}/pedigree-attributes`,
      body,
      options,
    ),

  /**
   * PATCH /persons/{guid}/type — set the person's type from the pedigree node editor.
   * `patient=true` makes it a PATIENT record; `patient=false` a pedigree-only INDIVIDUAL (PTV2-1157).
   */
  updatePersonType: (
    guid: string,
    patient: boolean,
    options?: { signal?: AbortSignal },
  ): Promise<void> =>
    http
      .patch<unknown>(
        `/persons/${encodeURIComponent(guid)}/type?patient=${String(patient)}`,
        undefined,
        options,
      )
      .then(() => undefined),

  /** PUT /persons/{guid}/demographics — replace the demographics blob. */
  updateDemographics: (
    guid: string,
    body: LiveDemographicsBody,
    options?: { signal?: AbortSignal },
  ): Promise<{ success?: boolean }> =>
    http.put<{ success?: boolean }>(
      `/persons/${encodeURIComponent(guid)}/demographics`,
      body,
      options,
    ),

  /**
   * PATCH /persons/{guid}/metadata — partial update of program / care team /
   * referral source. Local stub (PTV2-975); the OpenAPI contract still has
   * PersonMetadata as read-only. Body fields all optional — only the keys
   * present are written.
   */
  updatePatientMetadata: (
    guid: string,
    body: Partial<PersonMetadataWithCareTeamDto>,
    options?: { signal?: AbortSignal },
  ): Promise<PersonMetadataWithCareTeamDto> =>
    http.patch<PersonMetadataWithCareTeamDto>(
      `/persons/${encodeURIComponent(guid)}/metadata`,
      body,
      options,
    ),
};
